import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Building2, Caravan, ChartColumn, ClipboardCheck, Percent, Home, KeyRound, Loader2, LogIn, ScanLine, Search, ShieldCheck, Users, Wallet, Warehouse, Wrench } from 'lucide-react';
import { useAuthStore, homePathFor } from './store/useAuthStore';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AppShell from './components/layout/AppShell';
import LoginPage from './pages/LoginPage';
import SearchPage from './pages/SearchPage';
import HomePage from './pages/HomePage';
import FacilityDetailPage from './pages/FacilityDetailPage';
import ProfilePage from './pages/ProfilePage';
import RegisterPage from './pages/RegisterPage';
import AccountHome from './pages/account/AccountHome';
import VehiclesPage from './pages/account/VehiclesPage';
import BillingPage from './pages/account/BillingPage';
import LeasesPage from './pages/account/LeasesPage';
import AdminHome from './pages/admin/AdminHome';
import PlatformHome from './pages/platform/PlatformHome';
import UnitsPage from './pages/admin/UnitsPage';
import NewFacilityPage from './pages/admin/NewFacilityPage';
import EditFacilityPage from './pages/admin/EditFacilityPage';
import AddUnitsPage from './pages/admin/AddUnitsPage';
import DelinquencyPage from './pages/admin/DelinquencyPage';
import StaffPage from './pages/admin/StaffPage';
import TenantsPage from './pages/admin/TenantsPage';
import PlatformChargesPage from './pages/admin/PlatformChargesPage';
// Lazy: this is the only screen that pulls in recharts, and loading it
// eagerly would put ~120KB of charting in front of every public search.
const ReportsPage = lazy(() => import('./pages/admin/ReportsPage'));
import TenantDetailPage from './pages/admin/TenantDetailPage';
import SetPasswordPage from './pages/SetPasswordPage';
import GateHome from './pages/attendant/GateHome';
import GatePage from './pages/attendant/GatePage';
import AuditPage from './pages/attendant/AuditPage';
import ArrivalsPage from './pages/attendant/ArrivalsPage';
import TicketsPage from './pages/attendant/TicketsPage';
import CredentialsPage from './pages/attendant/CredentialsPage';
import FacilitySwitcher from './components/attendant/FacilitySwitcher';
import { useShiftStore } from './store/useShiftStore';

/**
 * A platform admin sees the operator console too — they support the owner, so
 * they need what the owner sees. The platform tab is theirs alone.
 */
const PLATFORM_NAV = [
  { to: '/platform', label: 'Platform', Icon: Percent, end: true },
  { to: '/admin', label: 'Operations', Icon: Home },
  { to: '/admin/reports', label: 'Reports', Icon: ChartColumn },
  { to: '/admin/tenants', label: 'Tenants', Icon: Users },
  { to: '/gate', label: 'Gate', Icon: ScanLine },
];

const CUSTOMER_NAV = [
  { to: '/account', label: 'Overview', Icon: Home, end: true },
  { to: '/account/spaces', label: 'My spaces', Icon: Warehouse },
  { to: '/account/vehicles', label: 'Vehicles', Icon: Caravan },
  { to: '/account/billing', label: 'Billing', Icon: Wallet },
  { to: '/search', label: 'Find a space', Icon: Search },
];

const ATTENDANT_NAV = [
  { to: '/gate', label: 'On shift', Icon: Home, end: true },
  { to: '/gate/verify', label: 'Verify', Icon: ScanLine },
  { to: '/gate/arrivals', label: 'Arrivals', Icon: LogIn },
  { to: '/gate/audits', label: 'Audits', Icon: ClipboardCheck },
  { to: '/gate/credentials', label: 'Access', Icon: KeyRound },
  { to: '/gate/tickets', label: 'Issues', Icon: Wrench },
  { to: '/platform', label: 'Platform', Icon: Percent, when: 'can_manage_platform' },
];

const ADMIN_NAV = [
  { to: '/admin', label: 'Operations', Icon: Home, end: true },
  { to: '/admin/reports', label: 'Reports', Icon: ChartColumn },
  { to: '/admin/delinquency', label: 'Delinquency', Icon: Wallet },
  { to: '/admin/tenants', label: 'Tenants', Icon: Users },
  { to: '/admin/staff', label: 'Staff', Icon: ShieldCheck },
  { to: '/admin/platform-charges', label: 'Platform fees', Icon: Percent },
  { to: '/platform', label: 'Platform', Icon: Percent, when: 'can_manage_platform' },
  { to: '/gate', label: 'Gate', Icon: ScanLine },
];

export default function App() {
  const bootstrap = useAuthStore((s) => s.bootstrap);

  // One /auth/me on boot establishes the session for the whole app.
  useEffect(() => { bootstrap(); }, [bootstrap]);

  // The root lets the document scroll. It was h-screen + overflow-hidden for
  // the split-screen map, which needed a locked viewport so the map could fill
  // it while the list scrolled inside. The map is gone, and the lock was left
  // behind clipping every page taller than the window.

  return (
    <div className="min-h-screen">
      <Routes>
        {/* Public discovery — a visitor must be able to see prices before signing up. */}
        <Route path="/search" element={<SearchPage />} />
        <Route path="/facilities/:slug" element={<FacilityDetailPage />} />

        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />

        <Route
          path="/account/*"
          element={
            <ProtectedRoute minimumRole="customer">
              <AppShell nav={CUSTOMER_NAV}>
                <Routes>
                  <Route index element={<AccountHome />} />
                  <Route path="spaces" element={<LeasesPage />} />
                  <Route path="vehicles" element={<VehiclesPage />} />
                  <Route path="billing" element={<BillingPage />} />
                  <Route path="*" element={<Placeholder title="Coming next" />} />
                </Routes>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gate/*"
          element={
            <ProtectedRoute minimumRole="attendant">
              <AttendantShell>
                <Routes>
                  <Route index element={<GateHome />} />
                  <Route path="verify" element={<GatePage />} />
                  <Route path="arrivals" element={<ArrivalsPage />} />
                  <Route path="audits" element={<AuditPage />} />
                  <Route path="tickets" element={<TicketsPage />} />
                  <Route path="credentials" element={<CredentialsPage />} />
                  <Route path="*" element={<Placeholder title="Coming next" />} />
                </Routes>
              </AttendantShell>
            </ProtectedRoute>
          }
        />

        {/* Its own tree, not a tab inside /admin. The platform admin outranks
            the owner but gets no facility access from it — requireFacilityAccess
            exempts super_admin by name, so every facility screen 404s for them.
            Running the platform is not a licence to read a tenant's file. */}
        <Route
          path="/platform/*"
          element={
            <ProtectedRoute minimumRole="platform_admin">
              <AppShell nav={PLATFORM_NAV}>
                <Routes>
                  <Route index element={<PlatformHome />} />
                  <Route path="*" element={<Placeholder title="Coming next" />} />
                </Routes>
              </AppShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/*"
          element={
            <ProtectedRoute minimumRole="admin">
              <AppShell nav={ADMIN_NAV}>
                <Routes>
                  <Route index element={<AdminHome />} />
                  <Route path="facilities/new" element={<NewFacilityPage />} />
                  <Route path="facilities/:facilityId/edit" element={<EditFacilityPage />} />
                  <Route path="facilities/:facilityId/units" element={<UnitsPage />} />
                  <Route path="facilities/:facilityId/units/new" element={<AddUnitsPage />} />
                  <Route path="delinquency" element={<DelinquencyPage />} />
                  <Route
                    path="reports"
                    element={
                      <Suspense fallback={
                        <div className="grid place-items-center py-20" role="status" aria-label="Loading reports">
                          <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
                        </div>
                      }>
                        <ReportsPage />
                      </Suspense>
                    }
                  />
                  <Route path="tenants" element={<TenantsPage />} />
                  <Route path="tenants/:userId" element={<TenantDetailPage />} />
                  <Route path="staff" element={<StaffPage />} />
                  <Route path="platform-charges" element={<PlatformChargesPage />} />
                  <Route path="*" element={<Placeholder title="Coming next" />} />
                </Routes>
              </AppShell>
            </ProtectedRoute>
          }
        />

        {/* Every signed-in role maintains their own account here. The shell's
            navigation adapts to the role, so there is one profile screen rather
            than one per console. */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute minimumRole="customer">
              <RoleShell>
                <ProfilePage />
              </RoleShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/set-password"
          element={
            <ProtectedRoute minimumRole="customer">
              <SetPasswordPage />
            </ProtectedRoute>
          }
        />

        <Route path="/" element={<HomeOrConsole />} />
        <Route path="*" element={<HomeRedirect />} />
      </Routes>
    </div>
  );
}

/** Wraps a shared screen in whichever console navigation the caller's role uses. */
function RoleShell({ children }) {
  const role = useAuthStore((s) => s.user?.role);
  const nav = role === 'customer' ? CUSTOMER_NAV
    : role === 'attendant' ? ATTENDANT_NAV
    : ADMIN_NAV;
  return <AppShell nav={nav}>{children}</AppShell>;
}

/**
 * The attendant shell loads their facility assignments once and exposes the
 * switcher, so no individual screen has to fetch or thread a facility id.
 */
function AttendantShell({ children }) {
  const status = useShiftStore((s) => s.status);
  const load = useShiftStore((s) => s.load);
  useEffect(() => { if (status === 'idle') load(); }, [status, load]);

  return (
    <AppShell nav={ATTENDANT_NAV} aside={<FacilitySwitcher />}>
      {children}
    </AppShell>
  );
}

/** Send each visitor to the console their role actually uses. */
/**
 * `/` used to redirect everyone to the map. A crawler followed that redirect
 * and indexed a filter panel, and a first-time visitor met a search UI before
 * being told what the place was. Signed-in users still go straight to their
 * console — they are not the audience for a marketing page.
 */
function HomeOrConsole() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  // Render the public page while the session resolves. Returning null here
  // would blank the most-visited page on every cold load, including for the
  // crawlers this page exists for.
  // Someone who types the domain wants their console; someone who deliberately
  // clicks the logo wants the page the logo points at. Without the distinction
  // the logo is inert for every signed-in user — it navigates to `/` and gets
  // bounced straight back, which reads as a broken link rather than a policy.
  // The intent rides in navigation state rather than the URL so `/` keeps one
  // canonical address for crawlers, and a refresh deliberately drops it.
  if (status === 'signed_in' && user && !location.state?.landing) {
    return <Navigate to={homePathFor(user.role)} replace />;
  }
  return <HomePage />;
}

function HomeRedirect() {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  if (status === 'unknown' || status === 'authenticating') return null;
  if (status !== 'signed_in') return <Navigate to="/search" replace />;
  return <Navigate to={homePathFor(user.role)} replace />;
}

function Placeholder({ title }) {
  return (
    <div className="mx-auto max-w-3xl px-4 py-12 text-center">
      <p className="text-sm font-bold text-slate-900">{title}</p>
      <p className="mt-1 text-sm text-slate-500">This screen is part of the next build stage.</p>
    </div>
  );
}
